import React from "react";
import { motion } from "framer-motion";

// Annotation - Small uppercase text with wide tracking
export const AnnotationText = ({
  children,
  className = "",
  variant = "default",
  color = "lightDark",
  as = "p",
  noSpacing = false,
  ...props
}) => {
  const variants = {
    default: "text-md sm:text-lg tracking-widest uppercase",
    heading: "text-xl sm:text-2xl tracking-widest uppercase",
    subtitle: "text-md sm:text-lg",
  };

  const spacing = {
    default: "mb-2",
    heading: "mb-4",
    subtitle: "",
  };

  const colors = {
    primary: "text-[var(--color-primary)]",
    secondary: "text-[var(--color-secondary)]",
    lightDark: "text-[var(--color-dark-gray)]",
    grayDark: "text-gray-900",
    gray: "text-gray-700",
  };

  const Component = as;

  return (
    <Component
      className={`${variants[variant]} ${colors[color]} ${
        !noSpacing ? spacing[variant] : ""
      } ${className}`}
      {...props}
    >
      {children}
    </Component>
  );
};

// Headline - Flexible headline component with semantic variants
export const Headline = ({
  children,
  className = "",
  variant = "h1",
  color = "primary",
  as,
  noSpacing = false,
  animate = false,
  ...props
}) => {
  const variants = {
    huge: {
      element: "h1",
      classes:
        "sm:text-[6rem] text-[3rem] text-left font-bold leading-[1.1] sm:leading-[1] ",

      spacing: "mb-6",
    },
    h1: {
      element: "h1",
      classes:
        "text-4xl sm:text-6xl leading-[1.5] sm:leading-[1.2] tracking-[var(--letter-spacing-normal)] font-semibold text-left",
      spacing: "mb-8",
    },
    h2: {
      element: "h2",
      classes:
        "text-3xl sm:text-5xl leading-[1.4] sm:leading-[1.2] tracking-[var(--letter-spacing-wider)] font-semibold text-left",
      spacing: "mb-2",
    },
    h3: {
      element: "h3",
      classes: "text-xl sm:text-2xl tracking-widest uppercase",
      spacing: "mb-4",
    },
    h3Offer: {
      element: "h3",
      classes: "text-xl sm:text-4xl tracking-wide uppercase",
      spacing: "mb-2 sm:mb-12",
    },
    h4: {
      element: "h4",
      classes: "text-3xl sm:text-3xl font-bold tracking-wider",
      spacing: "mb-4",
    },
    h5: {
      element: "h5",
      classes: "text-lg sm:text-2xl font-semibold",
      spacing: "mb-3",
    },
    h6: {
      element: "h6",
      classes: "text-md font-semibold",
      spacing: "mb-2",
    },
    "generic-card": {
      element: "h5",
      classes: "text-xl sm:text-2xl font-semibold tracking-wide",
      spacing: "mb-2",
    },
    light: {
      element: "h2",
      classes:
        "text-3xl sm:text-5xl leading-[1.4] sm:leading-[1.2] tracking-[var(--letter-spacing-wider)] text-left",
      spacing: "mb-6",
      style: { fontWeight: 300 },
    },
    splitscreen: {
      element: "h2",
      classes:
        "xl:text-[6rem] md:text-[4rem] text-[2rem] text-left font-bold leading-[1] sm:leading-[1]",
      spacing: "mb-6 sm:mb-16",
    },
    "splitscreen-heading": {
      element: "h3",
      classes: "text-2xl sm:text-3xl text-left font-bold mt-8 mb-1 sm:mb-4",
      spacing: "",
    },
  };

  const colors = {
    primary: "text-[var(--color-primary)]",
    secondary: "text-[var(--color-secondary)]",
    lightDark: "text-[var(--color-dark-gray)]",
    grayDark: "text-gray-900",
    gray: "text-gray-700",
  };

  const Component = as || variants[variant].element;

  // Animation variants for framer-motion
  const animationVariants = {
    hidden: {
      opacity: 0,
      y: 50,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const MotionComponent = motion(Component);

  if (animate) {
    return (
      <MotionComponent
        className={`${variants[variant].classes} ${colors[color]} ${
          !noSpacing ? variants[variant].spacing : ""
        } ${className}`}
        style={variants[variant].style || {}}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={animationVariants}
        {...props}
      >
        {children}
      </MotionComponent>
    );
  }

  return (
    <Component
      className={`${variants[variant].classes} ${colors[color]} ${
        !noSpacing ? variants[variant].spacing : ""
      } ${className}`}
      style={variants[variant].style || {}}
      {...props}
    >
      {children}
    </Component>
  );
};

// Paragraph - Flexible paragraph component with size variants
export const Paragraph = ({
  children,
  className = "",
  variant = "lead",
  color = "primary",
  noSpacing = false,
  ...props
}) => {
  const variants = {
    huge: {
      classes: "sm:text-4xl text-2xl sm:leading-relaxed leading-relaxed",
      spacing: "mb-6",
    },
    lead: {
      classes: "sm:text-3xl text-xl sm:leading-relaxed leading-relaxed",
      spacing: "mb-6",
    },
    large: {
      classes: "sm:text-2xl text-lg sm:leading-relaxed leading-relaxed",
      spacing: "mb-6",
    },
    medium: {
      classes: "sm:text-xl text-md leading-relaxed  sm:leading-relaxed",
      spacing: "mb-6",
    },
    base: {
      classes: "text-md sm:text-xl leading-[1.5] sm:leading-[1.5]",
      spacing: "mb-4",
    },
    small: {
      classes: "text-base leading-relaxed",
      spacing: "mb-4",
    },
    tiny: {
      classes: "text-tiny leading-relaxed",
      spacing: "mb-4",
    },
    "splitscreen-intro": {
      classes: "text-lg sm:text-2xl leading-relaxed",
      spacing: "mb-16",
    },
    "splitscreen-content": {
      classes: "text-lg sm:text-2xl leading-relaxed",
      spacing: "mb-16",
    },
  };

  const colors = {
    primary: "text-[var(--color-primary)]",
    secondary: "text-[var(--color-secondary)]",
    lightDark: "text-[var(--color-dark-gray)]",
    grayDark: "text-gray-900",
    gray: "text-gray-900",
  };

  return (
    <p
      className={`${variants[variant].classes} ${colors[color]} ${
        !noSpacing ? variants[variant].spacing : ""
      } ${className}`}
      {...props}
    >
      {children}
    </p>
  );
};

// Highlighted Quote - Large impactful text for quotes/callouts
export const HighlightedQuote = ({
  children,
  className = "",
  variant = "lead",
  color = "primary",
  showBorder = true,
  borderHeight = "standard",
  noSpacing = false,
  ...props
}) => {
  const variants = {
    lead: "sm:text-4xl text-2xl leading-[1.4] sm:leading-[1.4] italic",
    medium: "text-xl sm:text-3xl leading-[1.6] sm:leading-[1.6]",
  };

  const colors = {
    primary: "text-[var(--color-primary)]",
    secondary: "text-[var(--color-secondary)]",
    lightDark: "text-[var(--color-dark-gray)]",
    grayDark: "text-gray-900",
    gray: "text-gray-700",
  };

  const borderHeights = {
    standard: "h-44 sm:h-24",
    tall: "h-48 sm:h-24",
  };

  const defaultSpacing = "mb-6";

  if (showBorder) {
    return (
      <div className="flex items-center">
        <div
          className={`w-2 sm:w-2 ${borderHeights[borderHeight]} mr-6 sm:mr-4 flex-shrink-0`}
          style={{ backgroundColor: "var(--color-highlight)" }}
        ></div>
        <p
          className={`${variants[variant]} ${colors[color]} ${
            !noSpacing ? defaultSpacing : ""
          } ${className}`}
          {...props}
        >
          {children}
        </p>
      </div>
    );
  }

  return (
    <p
      className={`${variants[variant]} ${colors[color]} ${
        !noSpacing ? defaultSpacing : ""
      } ${className}`}
      {...props}
    >
      {children}
    </p>
  );
};
